import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../utils";
import { RequiredMark } from "../required-mark";
import type { CheckboxProps } from "./checkbox.types";

/**
 * Checkbox Component
 *
 * V&A Waterfront Design System - Checkbox
 *
 * A single checkbox with label, helper text, and error states.
 * Uses a hidden native input with a custom styled visual for consistent appearance.
 *
 * @example
 * // Basic usage
 * <Checkbox label="Accept terms" />
 *
 * // With error
 * <Checkbox label="Accept terms" errorText="You must accept" />
 *
 * // On dark background
 * <Checkbox label="Accept terms" onDark />
 *
 * // Indeterminate
 * <Checkbox label="Select all" indeterminate />
 *
 * // Small size
 * <Checkbox label="Option" size="sm" />
 *
 * // Label on the left
 * <Checkbox label="Accept terms" labelPosition="left" />
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>((t0, ref) => {
  const $ = _c(69);
  const {
    label,
    helperText,
    errorText,
    onDark: t1,
    className,
    disabled,
    id,
    name,
    checked,
    onChange,
    onBlur,
    required,
    value,
    indeterminate: t2,
    size: t3,
    labelPosition: t4
  } = t0;
  const onDark = t1 === undefined ? false : t1;
  const indeterminate = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "md" : t3;
  const labelPosition = t4 === undefined ? "right" : t4;
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descriptionId = `${inputId}-description`;
  const hasError = Boolean(errorText);
  const hasDescription = Boolean(errorText || helperText);
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = React.useState(checked ?? false);
  const isChecked = isControlled ? checked : internalChecked;
  const internalRef = React.useRef(null);
  let t5;
  let t6;
  if ($[0] !== indeterminate) {
    t5 = () => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    };
    t6 = [indeterminate];
    $[0] = indeterminate;
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[3] !== isControlled || $[4] !== onChange) {
    t7 = e => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e.target.checked);
    };
    $[3] = isControlled;
    $[4] = onChange;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handleChange = t7;
  const isMd = size === "md";
  const t8 = onDark && "dark";
  let t9;
  if ($[6] !== className || $[7] !== t8) {
    t9 = cn("group/checkbox flex w-full flex-col gap-2", t8, className);
    $[6] = className;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = disabled && "pointer-events-none";
  const t11 = labelPosition === "left" && "flex-row-reverse";
  let t12;
  if ($[9] !== t10 || $[10] !== t11) {
    t12 = cn("inline-flex cursor-pointer items-center gap-3", t10, t11);
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== ref) {
    t13 = node => {
      internalRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      }
    };
    $[12] = ref;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const t14 = hasError || undefined;
  const t15 = hasDescription ? descriptionId : undefined;
  let t16;
  if ($[14] !== disabled || $[15] !== handleChange || $[16] !== inputId || $[17] !== isChecked || $[18] !== name || $[19] !== onBlur || $[20] !== required || $[21] !== t13 || $[22] !== t14 || $[23] !== t15 || $[24] !== value) {
    t16 = <input ref={t13} type="checkbox" id={inputId} name={name} checked={isChecked} onChange={handleChange} disabled={disabled} required={required} value={value} aria-invalid={t14} aria-describedby={t15} onBlur={onBlur} className="peer sr-only" />;
    $[14] = disabled;
    $[15] = handleChange;
    $[16] = inputId;
    $[17] = isChecked;
    $[18] = name;
    $[19] = onBlur;
    $[20] = required;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = value;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = isMd ? "size-5 rounded-sm" : "size-4 rounded-[3px]";
  const t18 = !isChecked && !indeterminate && "hover:border-[#204795]";
  const t19 = (isChecked || indeterminate) && "border-[#204795] bg-[#204795]";
  const t20 = (isChecked || indeterminate) && "hover:bg-[#1a3a7a] hover:border-[#1a3a7a]";
  const t21 = (isChecked || indeterminate) && "[&>svg]:block";
  const t22 = disabled && "bg-[#f1f5f9] border-[#e2e8f0] opacity-50";
  const t23 = disabled && (isChecked || indeterminate) && "bg-[#94a3b8] border-[#94a3b8] opacity-50";
  const t24 = !isChecked && !indeterminate && "[.dark_&]:hover:border-[#204795]";
  const t25 = (isChecked || indeterminate) && "[.dark_&]:bg-[#204795] [.dark_&]:border-[#204795]";
  const t26 = (isChecked || indeterminate) && "[.dark_&]:hover:bg-[#1a3a7a] [.dark_&]:hover:border-[#1a3a7a]";
  const t27 = disabled && "[.dark_&]:bg-[#1e293b] [.dark_&]:border-[#334155]";
  let t28;
  if ($[26] !== t17 || $[27] !== t18 || $[28] !== t19 || $[29] !== t20 || $[30] !== t21 || $[31] !== t22 || $[32] !== t23 || $[33] !== t24 || $[34] !== t25 || $[35] !== t26 || $[36] !== t27) {
    t28 = cn("flex items-center justify-center border-[1.5px] transition-colors", t17, "border-[#e2e8f0] bg-white", t18, t19, t20, "peer-focus-visible:shadow-[0_0_0_2px_#204795]", "[&>svg]:hidden", t21, t22, t23, "[.dark_&]:bg-[#002b4c] [.dark_&]:border-[#334155]", t24, t25, t26, "[.dark_&]:peer-focus-visible:shadow-[0_0_0_2px_#1e40af]", t27);
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
    $[33] = t24;
    $[34] = t25;
    $[35] = t26;
    $[36] = t27;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== indeterminate || $[39] !== isMd) {
    t29 = indeterminate ? <Minus className={cn("text-white", isMd ? "size-3.5" : "size-3")} strokeWidth={3} /> : <Check className={cn("text-white", isMd ? "size-3.5" : "size-3")} strokeWidth={3} />;
    $[38] = indeterminate;
    $[39] = isMd;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] !== t28 || $[42] !== t29) {
    t30 = <span className={t28} aria-hidden="true">{t29}</span>;
    $[41] = t28;
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t16 || $[45] !== t30) {
    t31 = <span className="relative inline-flex shrink-0">{t16}{t30}</span>;
    $[44] = t16;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  const t32 = disabled && "text-input-text-disabled";
  let t33;
  if ($[47] !== t32) {
    t33 = cn("typography-body-sm-regular text-text-primary", t32);
    $[47] = t32;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== required) {
    t34 = <RequiredMark required={required} />;
    $[49] = required;
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  let t35;
  if ($[51] !== label || $[52] !== t33 || $[53] !== t34) {
    t35 = <span className={t33}>{label}{t34}</span>;
    $[51] = label;
    $[52] = t33;
    $[53] = t34;
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  let t36;
  if ($[55] !== t12 || $[56] !== t31 || $[57] !== t35) {
    t36 = <label className={t12}>{t31}{t35}</label>;
    $[55] = t12;
    $[56] = t31;
    $[57] = t35;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== descriptionId || $[60] !== errorText || $[61] !== hasDescription || $[62] !== hasError || $[63] !== helperText) {
    t37 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{errorText || helperText}</p>;
    $[59] = descriptionId;
    $[60] = errorText;
    $[61] = hasDescription;
    $[62] = hasError;
    $[63] = helperText;
    $[64] = t37;
  } else {
    t37 = $[64];
  }
  let t38;
  if ($[65] !== t36 || $[66] !== t37 || $[67] !== t9) {
    t38 = <div data-testid="checkbox" className={t9}>{t36}{t37}</div>;
    $[65] = t36;
    $[66] = t37;
    $[67] = t9;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  return t38;
});
Checkbox.displayName = "Checkbox";
export { Checkbox };